import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { DataSetComponent } from 'src/app/components/data-set/data-set.component';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import {
	AlertReportDialogComponent,
	AlertReportFilterOptions,
	AlertReportFilters,
} from '../alert-report-dialog/alert-report-dialog.component';
import { TimeZoneService } from 'src/app/services/timezone.service';
import { UserProfileService } from '../../../services/user-profile.service';
import { AppNavigationHeaderComponent } from '../../../components/navigation-header/app-navigation-header.component';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetPrimaryActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetContentComponent,
} from '../../../components/data-set/data-set.component';
import { BreadcrumbLabelDirective } from '../../../directives/breadcrumb-label.directive';
import { DataSetTableComponent } from '../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { DatePipe } from '@angular/common';
import {
	AlertReportFormatPipe,
	ShortTimezonePipe,
} from '../../../components/pipes';
import { DataSetLoadingRowComponent } from '../../../components/data-set/data-set-loading-row.component';

export type AlertReport = Components.Schemas.AlertReport;

@Component({
	selector: 'app-alert-report-table',
	templateUrl: './alert-report-table.component.html',
	styleUrls: ['./alert-report-table.component.scss'],
	imports: [
		AppNavigationHeaderComponent,
		DataSetComponent_1,
		BreadcrumbLabelDirective,
		DataSetToolbarComponent,
		DataSetPrimaryActionGroupDirective,
		DataSetActionButtonComponent,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		DatePipe,
		AlertReportFormatPipe,
		ShortTimezonePipe,
		DataSetLoadingRowComponent,
	],
})
export class AlertReportTableComponent implements AfterViewInit, OnDestroy {
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;

	displayedColumns = [
		'deviceSerialNumber',
		'deviceOwner',
		'deviceAlertType',
		'propertyName',
		'deviceLocation',
		'dateTime',
		'postalCode',
		'userName',
		'userCompany',
	];
	dataSource = new MatTableDataSource<AlertReport>();
	private filterSubscription?: Subscription;

	compiledFilters: QueryParam[] = [];
	hasFilters = false;
	reportFilters: AlertReportFilters;
	filterOptions?: AlertReportFilterOptions;

	breadcrumbLabel = $localize`:@@alertReportTable-breadcrumbLabel:Alert Report`;

	generateAlertReportLoadingKey = ApiLoadingKeys.generateAlertReport;
	generateAlertReportCsvLoadingKey = ApiLoadingKeys.generateAlertReportCsv;
	finishedLoadingInitData: boolean = false;

	isCareCompany = false;

	constructor(
		private readonly router: Router,
		private readonly dialog: MatDialog,
		private readonly apiService: ApiService,
		private readonly userProfileService: UserProfileService,
		public readonly loadingService: LoadingIndicatorService,
		readonly timeZoneService: TimeZoneService
	) {
		this.compiledFilters =
			this.router.lastSuccessfulNavigation?.extras?.state?.compiledFilters;
		this.reportFilters =
			this.router.lastSuccessfulNavigation?.extras?.state?.reportFilters;
		this.filterOptions =
			this.router.lastSuccessfulNavigation?.extras?.state?.filterOptions;
		this.hasFilters =
			this.router.lastSuccessfulNavigation?.extras?.state?.hasFilters;
	}

	ngAfterViewInit(): void {
		this.dataSet?.applyFilters();
		this.filterSubscription = this.dataSet?.queryParams
			.pipe(
				mergeMap(async (filter) => {
					const result = await this.apiService.generateAlertReport([
						...filter,
						...(this.compiledFilters ?? []),
					]);
					this.finishedLoadingInitData = true;
					return result;
				})
			)
			.subscribe((response) => {
				this.dataSource.data = response;
			});

		this.checkCareCompany()
			.then(() => {
				if (this.isCareCompany) {
					this.displayedColumns.splice(3, 0, 'clientName', 'clientIdn');
				}
			})
			.catch((error) =>
				console.error(
					'Something went wrong while checking care company:',
					error.message
				)
			);
	}

	async checkCareCompany(): Promise<void> {
		const userProfile = await this.userProfileService.getUserProfile();
		const activeCompanyId = userProfile?.activeRoleDetails?.company?.id;

		if (activeCompanyId) {
			const companyData = await this.apiService.getCompanyById(activeCompanyId);
			this.isCareCompany = companyData?.company?.companyType === 'care';
		}
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;
	}

	onRefineFiltersClicked(): void {
		this.dialog
			.open(AlertReportDialogComponent, {
				panelClass: 'drawer-dialog',
				data: {
					reportFilters: this.reportFilters,
					filterOptions: this.filterOptions,
				},
			})
			.afterClosed()
			.subscribe((_: any) => {
				this.dataSet.applyFilters();
			});
	}

	async onDownloadClicked(): Promise<void> {
		const fileName = this.hasFilters ? 'filteredAlertReport' : 'alertReport';
		await this.apiService.generateAlertReportCsv(
			fileName,
			this.compiledFilters
		);
	}

	rowId(row: AlertReport): string {
		return row?.deviceAlertId?.toString() || 'unknown-id';
	}
}
